import { cn } from '@buzzbee/ui';
import type { Product } from '../lib/session';

/**
 * The WRKRBZ mark (rounded hexagon with "WB" and the honey dot), shared by every WRKRBZ app.
 * Ink is currentColor so it follows the theme; the dot is always honey.
 */
export const MARK_PATHS = {
  hexagon:
    'M91.00 43.07Q95.00 50.00 91.00 56.93L76.50 82.04Q72.50 88.97 64.50 88.97L35.50 88.97Q27.50 88.97 23.50 82.04L9.00 56.93Q5.00 50.00 9.00 43.07L23.50 17.96Q27.50 11.03 35.50 11.03L64.50 11.03Q72.50 11.03 76.50 17.96Z',
  w: 'M23.5 36L30 64L36.5 44.96L43 64L49.5 36',
  b: 'M56 36V64M56 36H65C70.5 36 73 39 73 43C73 47 70.5 50 65 50H56M56 50H66.5C72 50 75 53 75 57C75 61 72 64 66.5 64H56',
} as const;

export const HONEY = '#F2C12E';

export const PRODUCT_NAMES: Record<Product, string> = { buzzbee: 'BuzzBee', receptionistbee: 'ReceptionistBee' };

/** The bare mark. `label` names it for screen readers; pass '' when text beside it already does. */
export function WrkrbzMark({ className, label = 'WRKRBZ' }: { className?: string; label?: string }) {
  return (
    <svg viewBox="0 0 100 100" className={cn('size-8 shrink-0 text-foreground', className)} {...(label ? { role: 'img', 'aria-label': label } : { 'aria-hidden': true })}>
      <path d={MARK_PATHS.hexagon} fill="none" stroke="currentColor" strokeWidth={3.2} />
      <path d={MARK_PATHS.w} fill="none" stroke="currentColor" strokeWidth={5.4} strokeLinecap="round" strokeLinejoin="round" />
      <path d={MARK_PATHS.b} fill="none" stroke="currentColor" strokeWidth={5.4} strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="72.5" cy="11" r={6.5} fill={HONEY} />
    </svg>
  );
}

/** The WRKRBZ mark with the product name beside it (BuzzBee, or ReceptionistBee for that tier). */
export function Logo({ product = 'buzzbee', compact, className }: { product?: Product; compact?: boolean; className?: string }) {
  const name = PRODUCT_NAMES[product];
  if (compact) return <WrkrbzMark className={className} label={name} />;
  return (
    <span className={cn('inline-flex items-center gap-2.5 font-display font-semibold tracking-[-0.02em] text-foreground', className)}>
      <WrkrbzMark label="" />
      <span className="text-[17px]">{name}</span>
    </span>
  );
}
